import { useEffect, useRef, useState } from 'react';
import { getStoredAccessToken } from './spotifyAuth';

function SpotifyPlayer() {
  const [deviceId, setDeviceId] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const playerRef = useRef(null);
  const initializedRef = useRef(false); // Prevent duplicate initialization
  
  // Use refs to track current state values for the global functions
  const deviceIdRef = useRef(null);
  const isReadyRef = useRef(false);

  // Update refs when state changes
  useEffect(() => {
    deviceIdRef.current = deviceId;
    isReadyRef.current = isReady;
  }, [deviceId, isReady]);

  useEffect(() => {
    // Prevent duplicate initialization in React Strict Mode
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;

    const initializeSpotifyPlayer = async () => {
      const token = await getStoredAccessToken();
      
      if (!token) {
        console.error('No Spotify token available');
        return;
      }

      // Load Spotify SDK script
      const script = document.createElement('script');
      script.src = 'https://sdk.scdn.co/spotify-player.js';
      script.async = true;
      document.body.appendChild(script);

      // Initialize player when SDK is ready
      window.onSpotifyWebPlaybackSDKReady = () => {
        console.log('Initializing Spotify Player...');
        
        const player = new window.Spotify.Player({
          name: 'Walkup Music App',
          getOAuthToken: cb => { cb(token); },
          volume: 0.5
        });

        // Error handling
        player.addListener('initialization_error', ({ message }) => {
          console.error('Initialization Error:', message);
        });

        player.addListener('authentication_error', ({ message }) => {
          console.error('Authentication Error:', message);
          console.log('Token may have expired. Please reconnect to Spotify.');
        });

        player.addListener('account_error', ({ message }) => {
          console.error('Account Error:', message);
        });

        player.addListener('playback_error', ({ message }) => {
          console.error('Playback Error:', message);
        });

        // Ready
        player.addListener('ready', ({ device_id }) => {
          console.log('Ready with Device ID', device_id);
          setDeviceId(device_id);
          setIsReady(true);
          
          // Store device ID globally so PlaylistView can use it
          window.spotifyDeviceId = device_id;
        });

        // Not Ready
        player.addListener('not_ready', ({ device_id }) => {
          console.log('Device ID has gone offline', device_id);
          setIsReady(false);
          window.spotifyDeviceId = null;
        });

        // Connect to the player
        player.connect().then(success => {
          if (success) {
            console.log('Successfully connected to Spotify!');
          }
        });

        playerRef.current = player;
      };
    };

    initializeSpotifyPlayer();

    // Cleanup
    return () => {
      if (playerRef.current) {
        playerRef.current.disconnect();
        playerRef.current = null;
      }
      window.spotifyDeviceId = null;
    };
  }, []);

  // Play function that can be called from anywhere
  window.playSpotifyTrack = async (uri, startTime = 0, duration = null) => {
    if (!deviceIdRef.current || !isReadyRef.current) {
      console.error('Player not ready');
      alert('Spotify player is not ready yet. Please wait a moment and try again.');
      return;
    }

    const token = await getStoredAccessToken();
    
    if (!token) {
      console.error('No token available for playback - token may have expired');
      alert('Your Spotify session has expired. Please click "Reconnect Spotify" at the top to continue playing music.');
      return;
    }
    
    try {
      // First, transfer playback to this device
      console.log('Transferring playback to device:', deviceIdRef.current);
      const transferResponse = await fetch('https://api.spotify.com/v1/me/player', {
        method: 'PUT',
        body: JSON.stringify({ 
          device_ids: [deviceIdRef.current],
          play: false
        }),
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });
      
      // Check if transfer was successful
      if (!transferResponse.ok && transferResponse.status !== 204) {
        if (transferResponse.status === 401) {
          alert('Your Spotify session has expired. Please click "Reconnect Spotify" at the top to continue.');
          return;
        }
        console.error('Device transfer failed:', transferResponse.status);
        throw new Error('Device not ready');
      }
      
      // Wait for device to activate on Spotify's servers
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Now play the track with position
      console.log(`Playing track: ${uri} from ${startTime}s for ${duration}s`);
      const response = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceIdRef.current}`, {
        method: 'PUT',
        body: JSON.stringify({ 
          uris: [uri],
          position_ms: startTime * 1000 // Convert seconds to milliseconds
        }),
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.ok || response.status === 204) {
        console.log('Playback started!');
        window.currentlyPlayingUri = uri;
        
        // If duration is specified, stop after that many seconds
        if (duration && duration > 0) {
          console.log(`Will stop after ${duration} seconds`);
          setTimeout(async () => {
            if (window.currentlyPlayingUri === uri) {
              await window.stopSpotifyTrack();
            }
          }, duration * 1000);
        }
      } else if (response.status === 401) {
        alert('Your Spotify session has expired. Please click "Reconnect Spotify" at the top to continue.');
      } else {
        const error = await response.json();
        console.error('Playback error:', error);
        alert('Could not play song. Your Spotify session may have expired. Please try reconnecting.');
      }
    } catch (error) {
      console.error('Error playing track:', error);
      alert('Error playing song. Your Spotify session may have expired. Please click "Reconnect Spotify" at the top.');
    }
  };

  // Stop function
  window.stopSpotifyTrack = async () => {
    if (!deviceIdRef.current || !isReadyRef.current) {
      console.error('Player not ready');
      return;
    }

    const token = await getStoredAccessToken();
    
    if (!token) {
      console.error('No token available');
      return;
    }
    
    try {
      console.log('Stopping playback');
      const response = await fetch(`https://api.spotify.com/v1/me/player/pause?device_id=${deviceIdRef.current}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.ok || response.status === 204) {
        console.log('Playback stopped!');
        window.currentlyPlayingUri = null;
      } else if (response.status === 404) {
        console.log('Device not found (404) - ignoring');
        window.currentlyPlayingUri = null;
      } else if (response.status === 401) {
        console.log('Token expired during stop - ignoring');
        window.currentlyPlayingUri = null;
      } else {
        console.error('Stop error:', response.status);
      }
    } catch (error) {
      console.error('Error stopping track:', error);
    }
  };

  // This component doesn't render anything visible
  return null;
}

export default SpotifyPlayer;